import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  ArrowRight, CheckCircle, BookOpen, Calculator, Brain, 
  BarChart3, ChevronLeft, ChevronRight, Lightbulb, Target,
  Zap, Clock, Award, FileText, Play, Check, Landmark, Monitor
} from "lucide-react";

const UniversalLearn = ({ examType = "csat", isPro, onBack, onPractice, defaultSection }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  
  // Navigation state
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubTopic, setSelectedSubTopic] = useState(null);
  
  // Content state
  const [content, setContent] = useState(null);
  const [progress, setProgress] = useState(null);
  const [defaultLoaded, setDefaultLoaded] = useState(false);

  // Icons for sections
  const sectionIcons = {
    quantitative_aptitude: Calculator,
    logical_reasoning: Brain,
    english_verbal: BookOpen,
    data_interpretation: BarChart3,
    banking_awareness: Landmark,
    computer_knowledge: Monitor
  };

  // Helper to get auth token
  const getToken = () => {
    try {
      const auth = JSON.parse(localStorage.getItem("ranker-auth"));
      return auth?.token || localStorage.getItem("token");
    } catch { return localStorage.getItem("token"); }
  };

  // Load sections on mount
  useEffect(() => {
    loadSections();
    loadProgress();
  }, []);

  // Auto-select default section if provided
  useEffect(() => {
    if (defaultSection && sections.length > 0 && !defaultLoaded) {
      const match = sections.find(s => s.id === defaultSection);
      if (match) {
        loadTopics(match.id);
        setDefaultLoaded(true);
      }
    }
  }, [defaultSection, sections, defaultLoaded]);

  const loadSections = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE}/universal-learn/sections?exam_type=${examType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(data.sections || []);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load sections" });
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE}/universal-learn/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(data);
    } catch (err) {
      console.error("Could not load progress");
    }
  };

  const loadTopics = async (sectionId) => {
    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE}/universal-learn/section/${sectionId}/topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopics(data.topics || []);
      setSelectedSection({ id: sectionId, name: data.section_name });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load topics" });
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async (subTopic) => {
    setContentLoading(true);
    setSelectedSubTopic(subTopic);
    toast({ description: "🔄 Generating learning content...", duration: 5000 });
    
    try {
      const token = getToken();
      const { data } = await axios.get(`${API_BASE}/universal-learn/content/${subTopic}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(data.content);
      toast({ description: data.cached ? "📚 Content loaded!" : "✨ Fresh content generated!" });
    } catch (err) {
      toast({ variant: "destructive", description: "Could not load content" });
    } finally {
      setContentLoading(false);
    }
  };

  const markCompleted = async () => {
    try {
      const token = getToken();
      await axios.post(`${API_BASE}/universal-learn/complete/${selectedSubTopic}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ description: "✅ Topic marked as completed!" });
      loadProgress();
      loadTopics(selectedSection.id);
    } catch (err) {
      toast({ variant: "destructive", description: "Could not save progress" });
    }
  };

  // Back navigation
  const handleBack = () => {
    if (content) {
      setContent(null);
      setSelectedSubTopic(null);
    } else if (selectedTopic) {
      setSelectedTopic(null);
    } else if (selectedSection) {
      setSelectedSection(null);
      setTopics([]);
    } else {
      onBack();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={handleBack}>
            ← Back
          </Button>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="px-4 py-2 text-sm border-blue-500 text-blue-600">
              <BookOpen className="w-3 h-3 mr-1" />
              Universal Learn
            </Badge>
            {progress && (
              <Badge variant="outline" className="px-4 py-2 text-sm border-emerald-500 text-emerald-600">
                {progress.overall_percentage}% Complete
              </Badge>
            )}
          </div>
        </div>

        {/* Title */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-500" />
            {content ? content.title : selectedSection ? selectedSection.name : "Learn Concepts"}
          </h1>
          <p className="text-slate-500 mt-1">
            {content 
              ? "Concepts, formulas & solved examples"
              : selectedSection 
                ? "Select a topic to start learning"
                : "Master concepts with shortcuts & exam-focused examples"
            }
          </p>
        </div>

        {/* Section Selection */}
        {!selectedSection && !loading && (
          <div className="space-y-6">
            <h2 className="text-base font-medium text-slate-700">Select a Section</h2>
            
            <div className="grid gap-4 md:grid-cols-2">
              {sections.map((section) => {
                const Icon = sectionIcons[section.id] || Target;
                const sectionProgress = progress?.section_progress?.[section.id];
                
                return (
                  <Card 
                    key={section.id}
                    className="group cursor-pointer border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
                    onClick={() => loadTopics(section.id)}
                    data-testid={`learn-section-${section.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-xl bg-blue-50 text-blue-600">
                          <Icon className="w-6 h-6" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {section.total_sub_topics} Topics
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-slate-900 text-lg mb-2">
                        {section.name}
                      </h3>
                      
                      {/* Progress bar */}
                      {sectionProgress && (
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>{sectionProgress.completed}/{sectionProgress.total} completed</span>
                            <span>{sectionProgress.percentage}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full transition-all"
                              style={{ width: `${sectionProgress.percentage}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center text-sm text-slate-500 group-hover:text-blue-600 transition-colors">
                        Start Learning
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Topic Selection */}
        {selectedSection && !content && !loading && (
          <div className="space-y-6">
            {topics.map((topic) => (
              <div key={topic.id} className="space-y-3">
                <h3 className="font-semibold text-slate-800 text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  {topic.name}
                </h3>
                
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {topic.sub_topics.map((st) => (
                    <Card 
                      key={st.id}
                      className={`group cursor-pointer transition-all ${
                        st.completed 
                          ? "border-emerald-200 bg-emerald-50/50" 
                          : "border-slate-200 hover:border-blue-300"
                      }`}
                      onClick={() => loadContent(st.id)}
                      data-testid={`learn-subtopic-${st.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600 transition-colors">
                            {st.name}
                          </span>
                          {st.completed ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                          ) : (
                            <Play className="w-4 h-4 text-slate-400 group-hover:text-blue-500" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Loading State */}
        {(loading || contentLoading) && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-lg font-semibold text-slate-900">
              {contentLoading ? "Generating Learning Content" : "Loading"}
            </p>
            <p className="text-sm text-slate-500">
              {contentLoading ? "CLARIAS is creating concepts, formulas & examples..." : "Please wait..."}
            </p>
          </div>
        )}

        {/* Content Display */}
        {content && !contentLoading && (
          <div className="space-y-8">
            
            {/* Introduction */}
            <Card className="border-blue-100 bg-blue-50/30">
              <CardContent className="p-6">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  Introduction
                </h3>
                <p className="text-slate-700">{content.introduction}</p>
              </CardContent>
            </Card>

            {/* Key Concepts */}
            {content.key_concepts?.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-500" />
                  Key Concepts
                </h3>
                <div className="grid gap-4">
                  {content.key_concepts.map((concept, idx) => (
                    <Card key={idx} className="border-slate-200">
                      <CardContent className="p-5">
                        <h4 className="font-semibold text-slate-800 mb-2">{concept.name}</h4>
                        <p className="text-slate-600 text-sm mb-2">{concept.explanation}</p>
                        {concept.formula && (
                          <div className="mt-2 p-3 bg-slate-100 rounded-lg font-mono text-sm">
                            {concept.formula}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Formulas */}
            {content.formulas?.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-blue-500" />
                  Important Formulas
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {content.formulas.map((f, idx) => (
                    <Card key={idx} className="border-blue-100 bg-blue-50/30">
                      <CardContent className="p-4">
                        <p className="font-semibold text-blue-900 text-sm mb-1">{f.name}</p>
                        <p className="font-mono text-slate-800 bg-white px-3 py-2 rounded mb-2">{f.formula}</p>
                        <p className="text-xs text-slate-600">{f.when_to_use}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Shortcuts */}
            {content.shortcuts?.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Shortcuts & Tricks
                </h3>
                <div className="grid gap-3">
                  {content.shortcuts.map((s, idx) => (
                    <Card key={idx} className="border-amber-100 bg-amber-50/30">
                      <CardContent className="p-4">
                        <p className="font-medium text-amber-900 mb-1">{s.trick}</p>
                        <p className="text-sm text-slate-600 italic">Example: {s.example}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Solved Examples */}
            {content.solved_examples?.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-500" />
                  Solved Examples ({content.solved_examples.length})
                </h3>
                <div className="space-y-4">
                  {content.solved_examples.map((ex, idx) => (
                    <Card key={idx} className="border-emerald-100">
                      <CardContent className="p-5">
                        <Badge className="mb-3 bg-emerald-100 text-emerald-800">
                          Example {idx + 1}
                        </Badge>
                        
                        <p className="font-medium text-slate-900 mb-4">{ex.question}</p>
                        
                        <div className="bg-slate-50 p-4 rounded-lg mb-3">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Solution:</p>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{ex.solution}</p>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                            Answer: {ex.answer}
                          </Badge>
                          {ex.time_saving_tip && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {ex.time_saving_tip}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Common Mistakes */}
            {content.common_mistakes?.length > 0 && (
              <Card className="border-red-100 bg-red-50/30">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-red-900 mb-3">⚠️ Common Mistakes to Avoid</h3>
                  <ul className="space-y-2">
                    {content.common_mistakes.map((m, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="text-red-500 mt-0.5">•</span>
                        {m}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Exam Tips */}
            {content.exam_tips?.length > 0 && (
              <Card className="border-purple-100 bg-purple-50/30">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-purple-900 mb-3">💡 Exam Tips</h3>
                  <ul className="space-y-2">
                    {content.exam_tips.map((t, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="text-purple-500 mt-0.5">✓</span>
                        {t}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-100">
              <Button 
                onClick={markCompleted}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="mark-completed-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Mark as Completed
              </Button>
              
              {onPractice && (
                <Button 
                  onClick={() => onPractice(selectedSubTopic)}
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  data-testid="practice-btn"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Practice This Topic
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalLearn;
